import { Box, Button, Grid, styled, Typography, useTheme } from '@mui/material';
import FilterSidebar from 'components/filter-sidebar';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Paper from 'components/paper';

import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useMemo } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import _ from 'underscore';
import { OrderFeedProvider } from 'views/order-feed/context/order-feed-context';
import { ProductDeclarationParams } from 'views/order-feed/features/order-history/types/product-declaration-params.type';
import { ProductDeclarationComponentGroup } from 'views/order-feed/features/order-history/types/product-declaration.type';
import OrderFeedTabsHeader from '../../../components/order-feed-tabs-header';
import { getProductDeclaration } from '../api/get-product-declaration';

const Line = styled('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1, 0),
  borderBottom: `1px solid ${theme.palette.lightGray.light}`,
  justifyContent: 'space-between',
  alignItems: 'flex-start',

  '&:nth-last-of-type(1)': {
    borderBottom: 'none',
  },

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
}));

const StyledGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '2rem',

  [theme.breakpoints.up('md')]: {
    flexWrap: 'wrap',
    flexDirection: 'row',
  },
}));

type Props = {};

const ProductDeclaration: React.FC<Props> = () => {
  const PAGE_NAME_ID = 'orderFeed.viewOrder.productDeclaration';
  const { account } = useAccountState();
  const params = useParams<ProductDeclarationParams>();
  const intl = useIntl();
  const theme = useTheme();
  const location = useLocation();
  /*
    Note: This is an ASC-specific background color, which is not part of the BluViu Style Guide.
    As a result, it is defined here separately rather than within the theme.
  */
  const AscBgColor = '#E0F0EE';
  const searchParams = new URLSearchParams(location.search);
  const ascCompliant = searchParams.get('ascCompliant') === 'true';

  // #region FETCH DATA

  const { data: productDeclaration } = useGet(
    ['getProductDeclaration', account.company?.customerErpId, params.orderNumber],
    ({ queryKey }) => getProductDeclaration(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.customerErpId && !!params.orderNumber,
    }
  );

  const batches = useMemo(
    () => productDeclaration?.batches.filter((batch) => batch.lineNumber === params.lineNumber),
    [params.lineNumber, productDeclaration]
  );

  // #region HELPERS

  const createPDF = async () => {
    const element = document.getElementById('prod-decl');
    if (!element) return;

    const canvas = await html2canvas(element, {
      scale: 1.2,
      backgroundColor: null,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'px', 'a4');

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const scaleRatio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);

    const pdfWidth = imgWidth * scaleRatio;
    const pdfHeight = imgHeight * scaleRatio;

    const marginX = (pageWidth - pdfWidth) / 2;
    const marginY = (pageHeight - pdfHeight) / 2;

    pdf.addImage(imgData, 'PNG', marginX, marginY, pdfWidth, pdfHeight);
    pdf.save(`product_declaration_${params.orderNumber}_${params.lineNumber}.pdf`);
  };

  // #endregion HELPERS

  return (
    <>
      <OrderFeedTabsHeader />
      <Paper transparent>
        <FilterSidebar>
          <Button size="small" onClick={() => createPDF()}>
            <FormattedMessage id={`general.exportPDFBtn`} />
          </Button>
        </FilterSidebar>
        <Grid container spacing={4}>
          <Grid size={12}>
            <Paper id="prod-decl" sx={{ width: '210mm', margin: 'auto' }}>
              {!batches ? (
                <Grid sx={{ background: '#EEF3FA', padding: '5px 15px' }}>
                  <Line sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography>Loading...</Typography>
                  </Line>
                </Grid>
              ) : (
                <>
                  {!batches.length ? (
                    <Typography>
                      <FormattedMessage id={`${PAGE_NAME_ID}.noBatches`} />
                      {params.lineNumber}
                    </Typography>
                  ) : (
                    batches?.map((batch, index) => {
                      let componentGroups = _.chain(batch.productComponents)
                        .filter((pc) => !_.isNull(pc.target))
                        .groupBy('componentGroup')
                        .value();

                      Object.keys(ProductDeclarationComponentGroup).map(
                        (group) => (componentGroups[group as string] = componentGroups[group as string] ?? [])
                      );

                      return (
                        <Paper key={batch.batchNumber + batch.lineNumber + +'-' + index}>
                          <Line sx={{ p: theme.spacing(5, 2) }}>
                            <Typography variant="h3">{batch?.itemDescription || batch?.itemName}</Typography>
                            <Typography>
                              <FormattedMessage id={`${PAGE_NAME_ID}.productNumber`} />
                              {parseInt(batch?.itemNumber)}
                            </Typography>
                          </Line>
                          <StyledGrid
                            sx={{
                              background: '#EEF3FA',
                              p: theme.spacing(1, 3),
                              display: 'flex',
                              alignItems: 'flex-end',
                              justifyContent: 'space-between',
                            }}>
                            <Typography sx={{ fontWeight: '700' }}>
                              <Line sx={{ borderBottom: '0px' }}>
                                <FormattedMessage id={`${PAGE_NAME_ID}.produced`} />
                                {batch?.produced ? (
                                  <FormattedDate value={batch?.produced} day="2-digit" month="short" year="numeric" />
                                ) : (
                                  <Line></Line>
                                )}
                              </Line>
                              <Line>
                                <FormattedMessage id={`${PAGE_NAME_ID}.bestBefore`} />
                                {batch?.bestbefore ? (
                                  <FormattedDate value={batch.bestbefore} day="2-digit" month="short" year="numeric" />
                                ) : (
                                  <Line></Line>
                                )}
                              </Line>
                            </Typography>
                            <Typography>
                              <Line>
                                <FormattedMessage id={`${PAGE_NAME_ID}.series`} />
                                {batch?.batchNumber}
                              </Line>
                            </Typography>
                          </StyledGrid>
                          <StyledGrid size={12} p={2}>
                            <Grid size={6}>
                              <Typography>
                                <FormattedMessage id={`${PAGE_NAME_ID}.customer`} />
                                {productDeclaration?.customerName}
                              </Typography>
                              <Typography>
                                <FormattedMessage id={`${PAGE_NAME_ID}.orderNumber`} />
                                {productDeclaration?.orderNumber}
                              </Typography>
                              <Typography>
                                <FormattedMessage id={`${PAGE_NAME_ID}.deliveredQuantity`} />
                                {batch?.deliveredQuantity ? <FormattedNumber value={batch.deliveredQuantity} /> : ''}
                              </Typography>
                            </Grid>
                            <Grid size={6}>
                              <Typography>
                                <FormattedMessage id={`${PAGE_NAME_ID}.producer`} />
                                {batch?.producerName}, {batch?.producerAddress}
                              </Typography>
                              <Typography>
                                <FormattedMessage id={`${PAGE_NAME_ID}.producedBy`} />
                                {batch?.producerNo}
                              </Typography>
                              <Typography>
                                <FormattedMessage id={`${PAGE_NAME_ID}.designation`} />
                                {batch?.designation}
                              </Typography>
                              <Typography>
                                <FormattedMessage id={`${PAGE_NAME_ID}.instruction`} />
                                {batch?.instruction}
                              </Typography>
                            </Grid>
                            <Grid
                              container
                              rowSpacing={1}
                              columnSpacing={{ xs: 6, sm: 2, md: 3 }}
                              sx={{ padding: '10px 0 20px', borderBottom: 'solid 1px grey' }}>
                              <Grid size={6} p={theme.spacing(5, 0)}>
                                <Typography>{batch?.labelText}</Typography>
                              </Grid>
                            </Grid>
                          </StyledGrid>
                          <Grid size={12} sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Grid size={{ xs: 8, md: 4 }} p={2}>
                              {!!componentGroups[ProductDeclarationComponentGroup.analytical].length && (
                                <List
                                  options={[
                                    { id: 'left', label: '' },
                                    { id: 'right', label: '' },
                                  ]}>
                                  <ListRow key={`${batch.batchNumber}_${batch.lineNumber}_analytical`}>
                                    <ListCell borderSize={2} colSpan={2}>
                                      <FormattedMessage id={`${PAGE_NAME_ID}.analyticalComponents`} />
                                    </ListCell>
                                  </ListRow>
                                  {componentGroups[ProductDeclarationComponentGroup.analytical].map((analytical) => (
                                    <ListRow key={`${batch.batchNumber}_${batch.lineNumber}_analytical_${analytical.componentName}`}>
                                      <ListCell borderSize={2} sx={{ borderRight: 'solid 2px white' }}>
                                        {analytical.componentName}
                                      </ListCell>
                                      <ListCell borderSize={2} align="right">
                                        <FormattedNumber
                                          value={analytical.target}
                                          minimumFractionDigits={1}
                                          maximumFractionDigits={1}
                                        />
                                        &nbsp;{analytical.unit}
                                      </ListCell>
                                    </ListRow>
                                  ))}
                                </List>
                              )}
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }} p={2}>
                              {_.any(componentGroups, (group) => !!group.length) && (
                                <List
                                  options={[
                                    {
                                      id: 'left',
                                      label: '',
                                    },
                                    {
                                      id: 'right',
                                      label: '',
                                      align: 'right',
                                    },
                                  ]}>
                                  <ListRow>
                                    <ListCell borderSize={2} colSpan={2}>
                                      <FormattedMessage id={`${PAGE_NAME_ID}.additives`} />
                                    </ListCell>
                                  </ListRow>
                                  {!!componentGroups[ProductDeclarationComponentGroup.vitamin].length && (
                                    <ListRow>
                                      <ListCell borderSize={2} colSpan={2}>
                                        <FormattedMessage id={`${PAGE_NAME_ID}.vitamins`} />
                                      </ListCell>
                                    </ListRow>
                                  )}
                                  {componentGroups[ProductDeclarationComponentGroup.vitamin].map((vitamin) => (
                                    <ListRow key={`${batch.batchNumber}_${batch.lineNumber}_${vitamin.target}`}>
                                      <ListCell borderSize={2} sx={{ borderRight: 'solid 2px white' }}>
                                        {vitamin.componentName}
                                      </ListCell>
                                      <ListCell borderSize={2} align="right">
                                        {`${vitamin.target} ${vitamin.unit}`}
                                      </ListCell>
                                    </ListRow>
                                  ))}
                                  {(!!componentGroups[ProductDeclarationComponentGroup?.mikronutrientPart1].length ||
                                    !!componentGroups[ProductDeclarationComponentGroup?.mikronutrientPart3].length) && (
                                    <ListRow>
                                      <ListCell borderSize={2} colSpan={2}>
                                        <FormattedMessage id={`${PAGE_NAME_ID}.micronutrients`} />
                                      </ListCell>
                                    </ListRow>
                                  )}
                                  {componentGroups[ProductDeclarationComponentGroup?.mikronutrientPart1].map(
                                    (mikronutrientPart1, index) => (
                                      <ListRow
                                        key={`${batch.batchNumber}_${batch.lineNumber}_${mikronutrientPart1.unit}_mikronutrientPart1-part1-${index}`}>
                                        <ListCell borderSize={2} sx={{ borderRight: 'solid 2px white' }}>
                                          {mikronutrientPart1.componentName}
                                        </ListCell>
                                        <ListCell borderSize={2} align="right">
                                          {`${mikronutrientPart1.target} ${mikronutrientPart1.unit}`}
                                        </ListCell>
                                      </ListRow>
                                    )
                                  )}
                                  {componentGroups[ProductDeclarationComponentGroup?.mikronutrientPart3].map(
                                    (mikronutrientPart3, index) => (
                                      <ListRow
                                        key={`${batch.batchNumber}_${batch.lineNumber}_${mikronutrientPart3.componentGroup.length}-part3-${index}`}>
                                        <ListCell borderSize={2} sx={{ borderRight: 'solid 2px white' }}>
                                          {mikronutrientPart3.componentName}
                                        </ListCell>
                                        <ListCell borderSize={2} align="right">
                                          {`${mikronutrientPart3.target} ${mikronutrientPart3.unit}`}
                                        </ListCell>
                                      </ListRow>
                                    )
                                  )}
                                  {!!componentGroups[ProductDeclarationComponentGroup.pigment].length && (
                                    <ListRow>
                                      <ListCell borderSize={2} colSpan={2}>
                                        <FormattedMessage id={`${PAGE_NAME_ID}.pigments`} />
                                      </ListCell>
                                    </ListRow>
                                  )}
                                  {componentGroups[ProductDeclarationComponentGroup.pigment].map((pigment) => (
                                    <ListRow key={`${batch.batchNumber}_${batch.lineNumber}_Pigment${pigment.componentName}`}>
                                      <ListCell borderSize={2} sx={{ borderRight: 'solid 2px white' }}>
                                        {pigment.componentName}
                                      </ListCell>
                                      <ListCell borderSize={2} align="right">
                                        {`${pigment.target} ${pigment.unit}`}
                                      </ListCell>
                                    </ListRow>
                                  ))}
                                  <ListRow>
                                    <ListCell borderSize={2} colSpan={2}>
                                      <FormattedMessage id={`${PAGE_NAME_ID}.ingredients`} />
                                    </ListCell>
                                  </ListRow>
                                  {batch?.ingredients && (
                                    <>
                                      <ListRow>
                                        <ListCell borderSize={2} colSpan={2}>
                                          {batch?.ingredients}
                                        </ListCell>
                                      </ListRow>
                                    </>
                                  )}
                                </List>
                              )}
                            </Grid>
                          </Grid>
                          {ascCompliant && (
                            <Box display={'flex'} gap={4} borderRadius={2} p={4} sx={{ background: `${AscBgColor}` }}>
                              <Box display={'table-column'} alignContent={'center'}>
                                <Typography fontSize={10} color={'black'}>
                                  <FormattedMessage id={`${PAGE_NAME_ID}.asc.title`} />
                                </Typography>
                                <Typography fontSize={10} fontWeight={400} color={'black'}>
                                  {/* Note : Once we get site audit done then we will get this number, for now show SXXXX */}
                                  <FormattedMessage
                                    id={intl.formatMessage(
                                      { id: `${PAGE_NAME_ID}.asc.info` },
                                      {
                                        siteNumber: batch?.ascSiteId ?? '',
                                      }
                                    )}
                                  />
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      );
                    })
                  )}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

const ProductDeclarationContainer: React.FC = () => (
  <OrderFeedProvider>
    <ProductDeclaration />
  </OrderFeedProvider>
);

export default ProductDeclarationContainer;
